// Card payments through Mollie (sculpt-api PLAN-payments.md M3): an order's payment status, its attempts, and the
// card an organization saved.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAsync } from '../hooks/useAsync';
import { dateOnly, dateTime, money } from '../services/format';
import { Alert, Badge, Button, Card, Cell, Confirm, KeyValue, Row, Table, type Tone } from './ui';
import { ChargebackBadge } from './refund';
import type { Order, OrganizationAdmin, PaymentStatus } from '../types/api';

const TONE: Record<PaymentStatus, Tone> = { open: 'warn', pending: 'warn', authorized: 'warn', paid: 'good', failed: 'bad', canceled: 'neutral', expired: 'neutral' };

export function PaymentStatusBadge({ status }: { status: PaymentStatus }) {
  const { t } = useTranslation();
  return <Badge tone={TONE[status] ?? 'neutral'}>{t(`payments.status.${status}`)}</Badge>;
}

/** An order's payment cell: « Contestation » when a payment of it was charged back, « Carte » when a card paid it,
 *  else the latest attempt's status, else nothing. */
export function OrderPaymentBadge({ order }: { order: Pick<Order, 'provider' | 'payment_status'> & Partial<Pick<Order, 'charged_back'>> }) {
  const { t } = useTranslation();
  if (order.charged_back) return <ChargebackBadge />;
  if (order.provider === 'mollie' && order.payment_status === 'paid') return <Badge tone="good">{t('payments.byCard')}</Badge>;
  if (!order.payment_status) return <span className="text-faint">—</span>;
  return <PaymentStatusBadge status={order.payment_status} />;
}

/** « Paiements » / « Masquer » for an order with at least one attempt. */
export function PaymentsToggle({ order, open, onToggle }: { order: Pick<Order, 'id' | 'payment_status'>; open: boolean; onToggle: (id: number | null) => void }) {
  const { t } = useTranslation();
  if (!order.payment_status) return null;
  return (
    <Button onClick={() => onToggle(open ? null : order.id)} data-testid={`payments-${order.id}`}>
      {open ? t('orders.hidePayments') : t('orders.showPayments')}
    </Button>
  );
}

/** Every card payment attempt of an order, newest first, each with a link to Mollie's dashboard. */
export function OrderPayments({ orderId, onClose }: { orderId: number; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const { data, error, loading } = useAsync(() => api.orderPayments(orderId), [orderId]);
  return (
    <div className="mt-4">
      <Card title={t('payments.title', { id: orderId })} actions={<Button onClick={onClose}>{t('orders.hidePayments')}</Button>}>
        {error && <div className="mb-3"><Alert>{error}</Alert></div>}
        <Table head={[t('payments.attempt'), t('payments.kind'), t('orders.statusLabel'), t('payments.amount'), t('payments.card'), t('payments.failure'), t('payments.createdAt'), t('payments.paidAt'), t('payments.expiresAt'), '']} empty={loading ? t('common.loading') : t('payments.none')}>
          {(data ?? []).map((p) => (
            <Row key={p.id}>
              <Cell mono>{p.provider_ref}</Cell>
              <Cell>{p.sequence_type === 'recurring' ? t('payments.recurring') : t('payments.online')}</Cell>
              <Cell>
                <div className="flex flex-wrap gap-1">
                  <PaymentStatusBadge status={p.status} />
                  {p.charged_back_at && <ChargebackBadge amount={p.charged_back_amount} at={p.charged_back_at} currency={p.currency} />}
                </div>
              </Cell>
              <Cell mono>{money(p.amount, p.currency, l)}</Cell>
              <Cell>{p.card_label ? `${p.card_label}${p.card_country ? ` (${p.card_country})` : ''}` : '—'}</Cell>
              <Cell>{p.failure_reason ?? '—'}</Cell>
              <Cell mono>{dateTime(p.created_at, l)}</Cell>
              <Cell mono>{dateTime(p.paid_at, l)}</Cell>
              <Cell mono>{dateTime(p.expires_at, l)}</Cell>
              <Cell>
                {p.dashboard_url ? (
                  <a href={p.dashboard_url} target="_blank" rel="noreferrer" className="text-accent underline">
                    {t('payments.dashboard')}
                  </a>
                ) : '—'}
              </Cell>
            </Row>
          ))}
        </Table>
      </Card>
    </div>
  );
}

/** The card the organization saved at its last card payment (the mandate renewals are charged on, M4). With
 *  `onRevoke`, staff can revoke it, after a confirmation. */
export function SavedCard({ org, busy, onRevoke }: {
  org: Pick<OrganizationAdmin, 'card_label' | 'card_expires_on' | 'mollie_customer_id' | 'mollie_mandate_id'>;
  busy?: boolean;
  onRevoke?: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const [confirming, setConfirming] = useState(false);
  const revoke = onRevoke && org.mollie_mandate_id ? (
    <Button variant="danger" onClick={() => setConfirming(true)} disabled={busy} data-testid="revoke-card">
      {t('payments.revoke')}
    </Button>
  ) : undefined;
  return (
    <div className="mt-4">
      <Confirm
        open={confirming}
        title={t('payments.revokeTitle')}
        message={t('payments.revokeMessage', { card: org.card_label ?? '' })}
        confirmLabel={t('payments.revoke')}
        danger
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void onRevoke?.().then(() => setConfirming(false))}
      />
      <Card title={t('payments.cardTitle')} actions={revoke}>
        {!org.mollie_mandate_id && !org.mollie_customer_id ? (
          <p className="text-[13px] text-faint" data-testid="no-card">{t('payments.noCard')}</p>
        ) : (
          <KeyValue
            rows={[
              [t('payments.cardLabel'), org.card_label ?? '—'],
              [t('payments.cardExpires'), dateOnly(org.card_expires_on, l)],
              [t('payments.customer'), org.mollie_customer_id ? <span className="font-mono">{org.mollie_customer_id}</span> : '—'],
              [t('payments.mandate'), org.mollie_mandate_id ? <span className="font-mono">{org.mollie_mandate_id}</span> : '—'],
            ]}
          />
        )}
      </Card>
    </div>
  );
}
